/**
 * Sprint AI-MODULE v2.3 AI-64.
 *
 * Action-aware CTA derivation for VM citation cards. The AI assistant
 * NEVER executes vendor workflow transitions on the user's behalf —
 * every CTA on a VM card is a navigation to the page where the user
 * acts themselves. The button label reads as the action the user is
 * about to take, not as a description of the destination.
 *
 * Lookup key: (citation.kind, citation.entity_state, citation.role_hint).
 * Output: primary CTA + optional secondary. Always falls back to a
 * generic "Open …" so a brand-new state at least navigates somewhere.
 */
import type { CitationRef } from '../api/aiApi';

export interface VmCta {
  label: string;          // verb-led: "Open InfoSec review", "Submit for InfoSec"
  href: string;           // SPA route + anchor, or mailto:
  variant: 'primary' | 'secondary' | 'ghost';
  external?: boolean;     // true for mailto: + http(s) so we don't trap the click
}

export function resolveVmCtas(c: CitationRef): VmCta[] {
  switch (c.kind) {
    case 'vm_vendor':
      return vendorCtas(c);
    case 'vm_pr':
      return prCtas(c);
    case 'vm_contract':
      return [{ label: 'Open contract', href: c.url, variant: 'primary' }];
    case 'vm_po':
      return [{ label: 'Open PO', href: c.url, variant: 'primary' }];
    default:
      return [];
  }
}

function vendorCtas(c: CitationRef): VmCta[] {
  const state = c.entity_state ?? '';
  const role = c.role_hint ?? '';
  const base = c.url;

  // The InfoSec reviewer is on the hook: send them to the approval
  // section. Add a Nudge mailto if the requester is named and blocked.
  if (state === 'SECURITY_REVIEW' && role === 'infosec_approver') {
    const out: VmCta[] = [
      { label: 'Open InfoSec review', href: `${base}#infosec-review`, variant: 'primary' },
    ];
    const nudge = buildNudgeCta(c, 'InfoSec review still open — could you upload the remaining docs?');
    if (nudge) out.push(nudge);
    out.push({ label: 'Vendor 360', href: base, variant: 'ghost' });
    return out;
  }

  // The requester is on the hook (post-info-request bounce-back, or
  // initial draft): send them to the submit section.
  if (role === 'requester') {
    if (state === 'DRAFT' || state === 'START' || state === 'PENDING_INTAKE') {
      return [
        { label: 'Continue intake', href: base, variant: 'primary' },
      ];
    }
    if (state === 'WAITING_FOR_USER_DATA' || state === 'INFO_REQUEST_RETURN') {
      return [
        { label: 'Submit for InfoSec', href: `${base}#submit-for-review`, variant: 'primary' },
        { label: 'Upload compliance doc', href: `${base}#compliance-docs`, variant: 'secondary' },
      ];
    }
    if (state === 'RECERTIFICATION_DUE') {
      return [
        { label: 'Refresh InfoSec evidence', href: `${base}#compliance-docs`, variant: 'primary' },
      ];
    }
  }

  // Approved / terminal — just open the 360.
  if (state === 'VENDOR_APPROVED' || state === 'CANCELLED' || state === 'REJECTED') {
    return [{ label: 'Open vendor 360', href: base, variant: 'primary' }];
  }

  // Caller is the owner/approver but vendor isn't in their court right now
  // (e.g. WAITING_FOR_USER_DATA + caller is owner). Surface a Nudge so
  // they can ping the requester.
  if (role === 'infosec_approver' && c.current_blocker_role === 'requester') {
    const out: VmCta[] = [
      { label: 'Open vendor 360', href: base, variant: 'primary' },
    ];
    const nudge = buildNudgeCta(c, 'Vendor intake is waiting on you — anything I can help unblock?');
    if (nudge) out.push(nudge);
    return out;
  }

  return [{ label: 'Open vendor 360', href: base, variant: 'primary' }];
}

function prCtas(c: CitationRef): VmCta[] {
  const state = c.entity_state ?? '';
  const role = c.role_hint ?? '';
  const base = c.url;

  if (state === 'pending_approval' && role === 'approver') {
    return [
      { label: 'Review & approve', href: `${base}#approval`, variant: 'primary' },
      { label: 'View line items', href: `${base}#line-items`, variant: 'secondary' },
    ];
  }
  if (state === 'draft' && role === 'requester') {
    return [
      { label: 'Continue PR', href: base, variant: 'primary' },
    ];
  }
  if (state === 'approved' || state === 'completed' || state === 'closed') {
    return [{ label: 'Open PR', href: base, variant: 'primary' }];
  }
  return [{ label: 'Open PR', href: base, variant: 'primary' }];
}

function buildNudgeCta(c: CitationRef, body: string): VmCta | null {
  const email = c.current_blocker_user_email;
  if (!email) return null;
  const subject = `Vortex: ${c.label}`;
  const href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const who = c.current_blocker_user_name ?? 'requester';
  return { label: `Nudge ${who}`, href, variant: 'secondary', external: true };
}
